<template>
    <v-container>
        <v-row class="unavailability-periods__page-title">
            <span class="unavailability-periods__title color-grey-onfly">
                Informe os períodos de indisponibilidade
            </span>
            <span class="unavailability-periods__subtitle color-grey-onfly">
                Registre os intervalos em que seu hotel não estará disponível para novas reservas.
            </span>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-data-table
                    :class="{ 'campaign-rounded-table': isUpdateCampaignRendering }"
                    :headers="headers"
                    :items="displayPeriods"
                    :loading="loading"
                    :items-per-page="10"
                    no-data-text="Nenhum período cadastrado."
                >
                    <template v-slot:item.start_date="{ item }">
                        {{ formatDate(item.start_date) }}
                    </template>
                    <template v-slot:item.end_date="{ item }">
                        {{ formatDate(item.end_date) }}
                    </template>
                    <template v-slot:item.total_days="{ item }">
                        {{ calculateTotalDays(item.start_date, item.end_date) }}
                    </template>
                    <template v-slot:item.status="{ item }">
                        <v-chip x-small :color="periodStatus(item).color" :dark="periodStatus(item).dark">
                            {{ periodStatus(item).label }}
                        </v-chip>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn icon small @click="openDeleteDialog(item)">
                            <v-icon color="error">mdi-delete-outline</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
            </v-col>

            <v-col cols="12">
                <v-card outlined class="mt-2 pa-5">
                    <p class="entry-label">Adicione um novo período de indisponibilidade:</p>

                    <v-form ref="addForm" v-model="addFormValid">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    max-width="290"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newStartDate"
                                            label="Data de início"
                                            outlined
                                            readonly
                                            :class="{ 'rounded-lg': isUpdateCampaignRendering }"
                                            :rules="[v => !!v || 'Campo obrigatório']"
                                            append-icon="mdi-calendar"
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newStartDate"
                                        no-title
                                        locale="pt-BR"
                                        :first-day-of-week="0"
                                        @input="startDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>

                            <v-col cols="12" md="6">
                                <v-menu
                                    v-model="endDateMenu"
                                    :close-on-content-click="false"
                                    max-width="290"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="newEndDate"
                                            label="Data de fim"
                                            outlined
                                            readonly
                                            :class="{ 'rounded-lg': isUpdateCampaignRendering }"
                                            :rules="endDateRules"
                                            append-icon="mdi-calendar"
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="newEndDate"
                                        no-title
                                        locale="pt-BR"
                                        :first-day-of-week="0"
                                        :min="newStartDate"
                                        @input="endDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                        </v-row>

                        <v-row class="mt-0">
                            <v-col cols="12">
                                <v-btn
                                    color="primary"
                                    :loading="addFormLoading"
                                    class="mr-3"
                                    @click="addPeriod"
                                >
                                    Salvar
                                </v-btn>
                                <v-btn outlined @click="clearForm">
                                    Limpar
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>

        <div class="actions-bar">
            <v-container class="pb-0 px-0">
                <v-container class="actions-bar__content-container pb-6">
                    <v-row class="d-flex justify-end">
                        <v-col cols="12" md="4" class="btn-column">
                            <v-btn
                                depressed
                                color="blue-grey"
                                @click="$emit('go-back')"
                                class="w-100 rounded-lg go-back-btn"
                            >
                                Voltar
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="4" class="btn-column">
                            <v-btn
                                depressed
                                color="success"
                                @click="submit"
                                class="w-100 rounded-lg"
                            >
                                Enviar
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-container>
        </div>

        <confirm-deletion-modal
            :dialog-active="deleteDialog.active"
            :dialog-loading="deleteDialog.loading"
            :entity-to-delete="deleteDialog.label"
            @delete-method="confirmDelete"
            @close-dialog="deleteDialog.active = false"
        />

        <unavailability-conflict-modal
            :active="conflictModal.active"
            :loading="conflictModal.loading"
            :pending-bookings="conflictModal.pendingBookings"
            :confirmed-bookings="conflictModal.confirmedBookings"
            @confirm="confirmConflictAndAdd"
            @cancel="closeConflictModal"
        />
    </v-container>
</template>

<script>
import hotelUnavailabilityPeriodService from '../../../services/hotelUnavailabilityPeriodService';
import ConfirmDeletionModal from '../../Modals/ConfirmDeletionModal.vue';
import UnavailabilityConflictModal from '../../Hotel/HotelForm/UnavailabilityConflictModal.vue';

export default {
    name: 'UnavailabilityPeriods',

    components: { ConfirmDeletionModal, UnavailabilityConflictModal },

    props: {
        tokenHash: {
            type: String,
            required: true,
        },
    },

    data: () => ({
        periods: [],
        periodsToAdd: [],
        periodIdsToDelete: [],
        isUpdateCampaignRendering: true,
        loading: false,
        addFormValid: false,
        addFormLoading: false,
        newStartDate: null,
        newEndDate: null,
        startDateMenu: false,
        endDateMenu: false,
        deleteDialog: {
            active: false,
            loading: false,
            label: '',
            periodId: null,
            localIndex: null,
        },
        conflictModal: {
            active: false,
            loading: false,
            pendingBookings: [],
            confirmedBookings: [],
        },
        pendingPeriodToAdd: null,
        headers: [
            { text: 'Data de início', value: 'start_date',  sortable: false },
            { text: 'Data de fim',    value: 'end_date',    sortable: false },
            { text: 'Total de dias',  value: 'total_days',  sortable: false },
            { text: 'Status',         value: 'status',      sortable: false },
            { text: '',               value: 'actions',     sortable: false, align: 'end' },
        ],
    }),

    computed: {
        endDateRules() {
            return [
                v => !!v || 'Campo obrigatório',
                v => !this.newStartDate || v >= this.newStartDate || 'A data de fim deve ser igual ou posterior à data de início',
            ];
        },

        displayPeriods() {
            const persistedPeriods = this.periods.filter(period => {
                return !this.periodIdsToDelete.includes(period.id);
            });

            const localPeriods = this.periodsToAdd.map((period, index) => {
                return {
                    id: null,
                    start_date: period.start_date,
                    end_date: period.end_date,
                    _localIndex: index,
                };
            });

            return persistedPeriods.concat(localPeriods);
        },
    },

    mounted() {
        this.loadPeriods();
    },

    methods: {
        async loadPeriods() {
            try {
                this.loading = true;
                const res = await hotelUnavailabilityPeriodService.listByOwnerToken(this.tokenHash);
                this.periods = res.data.data;
            } catch (e) {
                this.$simpleDialog('Erro ao carregar os períodos de indisponibilidade.', 'error');
            } finally {
                this.loading = false;
            }
        },

        clearForm() {
            this.newStartDate = null;
            this.newEndDate = null;
            this.$refs.addForm.reset();
        },

        async addPeriod() {
            if (!this.$refs.addForm.validate()) return;
            try {
                this.addFormLoading = true;
                const res = await hotelUnavailabilityPeriodService.checkConflictsByOwnerToken(
                    this.tokenHash,
                    this.newStartDate,
                    this.newEndDate
                );
                const pending = res.data.data.pending || [];
                const confirmed = res.data.data.confirmed || [];

                if (pending.length > 0 || confirmed.length > 0) {
                    this.pendingPeriodToAdd = {
                        start_date: this.newStartDate,
                        end_date: this.newEndDate,
                        force: false,
                    };
                    this.conflictModal.pendingBookings = pending;
                    this.conflictModal.confirmedBookings = confirmed;
                    this.conflictModal.active = true;
                    return;
                }

                this.commitPeriodToLocalList({
                    start_date: this.newStartDate,
                    end_date: this.newEndDate,
                    force: false,
                });
            } catch (e) {
                this.$simpleDialog('Erro ao verificar conflitos. Tente novamente.', 'error');
            } finally {
                this.addFormLoading = false;
            }
        },

        confirmConflictAndAdd() {
            this.pendingPeriodToAdd.force = true;
            this.commitPeriodToLocalList(this.pendingPeriodToAdd);
            this.closeConflictModal();
        },

        closeConflictModal() {
            this.conflictModal.active = false;
            this.conflictModal.loading = false;
            this.pendingPeriodToAdd = null;
        },

        commitPeriodToLocalList(period) {
            this.periodsToAdd.push(period);
            this.clearForm();
        },

        openDeleteDialog(period) {
            this.deleteDialog.periodId = period.id || null;
            this.deleteDialog.localIndex = period._localIndex !== undefined ? period._localIndex : null;
            this.deleteDialog.label = `${this.formatDate(period.start_date)} a ${this.formatDate(period.end_date)}`;
            this.deleteDialog.active = true;
        },

        confirmDelete() {
            if (this.deleteDialog.periodId !== null) {
                this.periodIdsToDelete.push(this.deleteDialog.periodId);
            } else if (this.deleteDialog.localIndex !== null) {
                this.periodsToAdd.splice(this.deleteDialog.localIndex, 1);
            }

            this.deleteDialog.active = false;
            this.deleteDialog.periodId = null;
            this.deleteDialog.localIndex = null;
            this.deleteDialog.label = '';
        },

        submit() {
            this.$emit('confirm-sending-form', {
                periodsToAdd: this.periodsToAdd,
                periodIdsToDelete: this.periodIdsToDelete,
            });
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const datePart = String(dateStr).substring(0, 10);
            const parts = datePart.split('-');
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        },

        calculateTotalDays(startDateStr, endDateStr) {
            if (!startDateStr || !endDateStr) return '';
            const start = new Date(String(startDateStr).substring(0, 10));
            const end = new Date(String(endDateStr).substring(0, 10));
            const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
            return days === 1 ? '1 dia' : `${days} dias`;
        },

        periodStatus(item) {
            const today = new Date().toISOString().substring(0, 10);
            const start = String(item.start_date).substring(0, 10);
            const end = String(item.end_date).substring(0, 10);
            if (end < today) return { label: 'Expirado', color: 'grey lighten-1', dark: false };
            if (start > today) return { label: 'Futuro', color: 'warning', dark: false };
            return { label: 'Ativo', color: 'success', dark: true };
        },
    },
};
</script>

<style lang="scss" scoped>
.unavailability-periods__page-title {
    display: flex;
    flex-direction: column;
    padding-left: 1rem;
    margin-bottom: 1rem;
    margin-top: 3rem;
}

.unavailability-periods__title {
    font-size: 1.125rem;
    font-weight: 600;
}

.unavailability-periods__subtitle {
    font-size: 0.875rem;
}

.entry-label {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 16px;
    letter-spacing: 0.01em;
}

.campaign-rounded-table::v-deep .v-data-table__wrapper {
    border-radius: 8px;
}

.go-back-btn {
    color: white !important;
}

.actions-bar {
    max-width: 1440px;
    margin: 0 auto;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    padding: 6px 12px 0 6px;
}

.actions-bar__content-container {
    padding: 1.5rem 1rem 0.5rem 1rem;
    background: white;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    border-top-left-radius: 2rem;
    border-top-right-radius: 2rem;
}

@media (max-width: 960px) {
    .actions-bar {
        .btn-column {
            padding-top: 0;
            padding-bottom: 0;
        }
    }
}
</style>
